import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Plus, Trash2, ArrowUp, ArrowDown, AlertCircle, Info, Sparkles } from 'lucide-react';
import WhatsAppMessagePreview from '@/components/WhatsAppMessagePreview';
import MediaAssetGrid from '@/components/MediaAssetGrid';
import { extractVariableIndices } from '@/lib/templateVariables';
import type { SendRequirements } from '@/lib/whatsappPreview';
import {
  TemplateDraft,
  TemplateMediaAsset,
  DraftButton,
  ButtonType,
  ASSET_KIND_FOR_HEADER,
  emptyDraft,
  validateDraft,
  errorsByField,
  slugifyTemplateName,
  insertVariableAt,
  normalizeDraftVariables,
  draftToPayload,
  optOutLabelsFor,
  relabelOptOut,
  CONTACT_FIELDS,
  TEMPLATE_LIMITS,
  TEMPLATE_CATEGORIES,
  TEMPLATE_LANGUAGES,
  HEADER_TYPES,
  MEDIA_HEADER_TYPES,
  SAMPLE_ACCEPT,
} from '@/lib/templateComposer';

// Authoring a WhatsApp template.
//
// Its own file rather than another block inside WhatsAppTemplates.tsx, which is already past 1300
// lines: this is ~450 more and it serves both Create and Edit. That is the same idea as the shared
// renderContactForm() in pages/Contacts.tsx, at a size that earns a file. Everything else follows the
// house pattern exactly — a controlled useState draft, a shadcn Dialog, and apiFetch in the caller.
//
// THE SAMPLE AND THE MAPPING SIT ON THE SAME ROW, per variable. That is the design point of the
// whole form. Everywhere else in this product the {{n}} -> contact field link has to be GUESSED from
// whatever examples the provider happened to store (services/templateVariableMapping.js exists for
// that, and declines more often than it matches). Here the author tells us both in one submission,
// so the guessing is bypassed entirely — and asking for the sample beside the field it maps to is
// what makes writing it feel purposeful rather than like a chore.
//
// The author may type {{name}} instead of {{1}}. The draft keeps what they typed; everything that
// leaves this form — validation, the preview, the submitted payload — reads the positional copy
// normalizeDraftVariables makes of it.

// Radix Select reserves '' for "no selection" and throws on an item using it.
const NO_FIELD = '__none__';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Absent for create; present to edit an existing template. */
  initial?: Partial<TemplateDraft> | null;
  mode: 'create' | 'edit';
  canUploadSamples: boolean;
  submitting: boolean;
  /** Uploads to POST /media-sample and resolves to Meta's opaque handle. */
  onUploadSample: (file: File, headerFormat: string) => Promise<{ handle: string; filename: string } | null>;
  onSubmit: (payload: ReturnType<typeof draftToPayload>) => Promise<void>;
  /**
   * The generated photos and videos of the media this draft was made from — offered as the header,
   * so nobody has to download a picture this product made for them only to upload it back.
   */
  mediaAssets?: TemplateMediaAsset[];
  /** That media's name, for the "pre-filled from" line. */
  sourceMediaName?: string | null;
  /**
   * Uploads one of those assets as the header sample (POST /media-sample/from-asset) and resolves to
   * Meta's handle. Rejects with the server's reason, which is shown beside the picture it is about.
   */
  onUseAsset?: (assetUid: string, headerFormat: string) => Promise<{ handle: string; filename: string }>;
}

const FieldError = ({ message }: { message?: string }) =>
  (message ? <p className="text-xs text-destructive">{message}</p> : null);

export default function TemplateComposerDialog({
  open,
  onOpenChange,
  initial,
  mode,
  canUploadSamples,
  submitting,
  onUploadSample,
  onSubmit,
  mediaAssets = [],
  sourceMediaName = null,
  onUseAsset,
}: Props) {
  const [draft, setDraft] = useState<TemplateDraft>({ ...emptyDraft, ...(initial || {}) });
  const [uploading, setUploading] = useState(false);
  // Turning a generated asset into Meta's sample happens on the server, while the user carries on.
  const [preparing, setPreparing] = useState(false);
  const [assetError, setAssetError] = useState<string | null>(null);
  // Only the latest pick may write its handle back: a slow answer for a picture somebody has since
  // swapped out would otherwise attach the wrong sample.
  const assetRequest = useRef(0);
  const [showUpload, setShowUpload] = useState(false);
  // Errors are only shown after a submit attempt. Annotating a form the user has not finished
  // filling in is noise, not help.
  const [touched, setTouched] = useState(false);
  // A local object URL of the chosen sample, for the preview alone. What reaches Meta is the opaque
  // handle, which nothing can render, so the file itself is the only way to show it.
  const [samplePreviewUrl, setSamplePreviewUrl] = useState<string | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  // Revokes the previous URL whenever it is replaced or cleared, and the last one on unmount — the
  // dialog is mounted only while open, so closing it releases the file.
  useEffect(() => () => {
    if (samplePreviewUrl) URL.revokeObjectURL(samplePreviewUrl);
  }, [samplePreviewUrl]);

  const patch = (changes: Partial<TemplateDraft>) => setDraft((d) => ({ ...d, ...changes }));

  // `variables` pairs each token as typed with the index it is submitted as, in body order.
  const { draft: normalized, variables } = useMemo(() => normalizeDraftVariables(draft), [draft]);

  const errors = useMemo(
    () => validateDraft(normalized, { canUploadSamples }),
    [normalized, canUploadSamples],
  );
  const errorFor = useMemo(() => errorsByField(errors), [errors]);
  const show = (field: string) => (touched ? errorFor[field] : undefined);
  // A body that arrived pre-filled from generated media is not half-typed, so an over-long one is
  // said straight away rather than after a submit that was always going to be refused.
  const prefilled = mode === 'create' && Boolean(initial?.campaignMediaId);
  const showBodyError = touched || prefilled ? errorFor.body : undefined;

  /** The assets that could fill a header of this type: photos for IMAGE, videos for VIDEO. */
  const assetsFor = (headerType: string) =>
    mediaAssets.filter((asset) => asset.kind === ASSET_KIND_FOR_HEADER[headerType]);
  const headerAssets = assetsFor(draft.headerType);
  const chosenAsset = headerAssets.find((asset) => asset.uid === draft.headerAssetUid) ?? null;

  // Pick a generated asset as the header. The handle comes back from the server, which fetches the
  // object itself and hands it to Meta; the picture shows at once, since it is already ours.
  const pickAsset = async (asset: TemplateMediaAsset, headerType: string) => {
    if (!onUseAsset || !asset.usable) return;
    const request = ++assetRequest.current;
    setAssetError(null);
    setPreparing(true);
    setShowUpload(false);
    setSamplePreviewUrl(null);
    patch({ headerAssetUid: asset.uid, headerPreviewUrl: asset.url, headerHandle: null, headerFilename: null });
    try {
      const result = await onUseAsset(asset.uid, headerType);
      if (request !== assetRequest.current) return;
      patch({ headerHandle: result.handle, headerFilename: result.filename });
    } catch (error) {
      if (request !== assetRequest.current) return;
      setAssetError(error instanceof Error ? error.message : 'Could not prepare that file for WhatsApp.');
    } finally {
      if (request === assetRequest.current) setPreparing(false);
    }
  };

  // A draft that opens with an asset as its header — pre-filled from media, or an edit of a
  // template made from it — still needs the handle Meta reviews. Requested once: the ref outlives
  // StrictMode's second effect run, which would otherwise upload the same picture twice.
  const autoRequested = useRef(false);
  useEffect(() => {
    if (autoRequested.current) return;
    autoRequested.current = true;
    if (!draft.headerAssetUid || draft.headerHandle) return;
    const asset = assetsFor(draft.headerType).find((a) => a.uid === draft.headerAssetUid);
    if (asset?.usable) {
      pickAsset(asset, draft.headerType);
    } else if (mediaAssets.length > 0) {
      // Gone from the media, or a type WhatsApp refuses: no default rather than a broken one. Only
      // decided when the media's assets actually loaded — a failed fetch proves nothing about it.
      patch({ headerAssetUid: null, headerPreviewUrl: null });
    }
    // Deliberately once, on open — see above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const bodyIndices = useMemo(() => extractVariableIndices(normalized.body), [normalized.body]);

  /** 'image', 'video' or 'document', for copy about a media header. */
  const mediaNoun = draft.headerType.toLowerCase();

  const handleInsertVariable = () => {
    const el = bodyRef.current;
    const cursor = el?.selectionStart ?? draft.body.length;
    const next = insertVariableAt(draft.body, cursor);
    patch({ body: next.body });
    // Restore the caret after React re-renders, so typing continues where it left off.
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(next.cursor, next.cursor);
    });
  };

  const handleSampleUpload = async (input: HTMLInputElement) => {
    const file = input.files?.[0];
    if (!file) return;
    // An asset still being prepared is superseded: the file chosen last is the sample.
    assetRequest.current += 1;
    setPreparing(false);
    setAssetError(null);
    // Shown at once rather than after the upload: the preview needs only the file, not Meta.
    setSamplePreviewUrl(URL.createObjectURL(file));
    setUploading(true);
    try {
      const result = await onUploadSample(file, draft.headerType);
      // A hand-picked file is a review sample only — it is not kept, so it cannot become the
      // campaigns' default header. The media link stays; the asset one goes.
      if (result) {
        patch({
          headerHandle: result.handle, headerFilename: result.filename, headerAssetUid: null, headerPreviewUrl: null,
        });
      }
      // Cleared on failure so that choosing the same file again is a change at all — the browser
      // fires nothing for re-selecting the file an input already holds.
      else {
        input.value = '';
        setSamplePreviewUrl(null);
      }
    } finally {
      setUploading(false);
    }
  };

  const updateButton = (index: number, changes: Partial<DraftButton>) => {
    patch({ buttons: draft.buttons.map((b, i) => (i === index ? { ...b, ...changes } : b)) });
  };

  const moveButton = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= draft.buttons.length) return;
    const next = [...draft.buttons];
    [next[index], next[target]] = [next[target], next[index]];
    patch({ buttons: next });
  };

  const handleSubmit = async () => {
    setTouched(true);
    if (errors.length > 0) return;
    await onSubmit(draftToPayload(normalized));
  };

  // The preview is a pure, props-only component, so a draft feeds it with no adapter. Passing the
  // samples as `variableLabels` is what makes it render "Hello Ahmed" rather than a bare chip —
  // the author reads the message the reviewer at Meta will read.
  const previewRequirements: SendRequirements = {
    header: draft.headerType === 'NONE' ? null : {
      format: draft.headerType,
      text: draft.headerType === 'TEXT' ? draft.headerContent : null,
      hasVariable: /\{\{\d+\}\}/.test(draft.headerContent),
      sampleMediaUrl: null,
    },
    bodyIndices,
    urlButtons: draft.buttons
      .map((b, index) => ({ b, index }))
      .filter(({ b }) => b.type === 'URL')
      .map(({ b, index }) => ({
        index,
        text: b.text,
        url: b.url ?? null,
        hasVariable: /\{\{\d+\}\}/.test(b.url ?? ''),
      })),
    hasCarousel: false,
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[1000px] max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'Create Template' : 'Edit Template'}</DialogTitle>
          <DialogDescription>
            {mode === 'create'
              ? 'WhatsApp reviews every template before it can be used. Approval usually takes a few minutes to a few hours.'
              : 'Saving sends this template back to WhatsApp for review, so it cannot be used until it is approved again.'}
          </DialogDescription>
          {sourceMediaName && mode === 'create' && (
            // Where the defaults came from, so nothing on the form looks invented.
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground" dir="auto">
              <Sparkles className="h-3.5 w-3.5 shrink-0" />
              Pre-filled from your media &ldquo;{sourceMediaName}&rdquo;. Change anything before you submit.
            </p>
          )}
        </DialogHeader>

        <div className="grid gap-6 sm:grid-cols-[1fr_320px]">
          {/* ---------------- form ---------------- */}
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select
                  value={draft.category}
                  onValueChange={(v) => patch({ category: v as TemplateDraft['category'] })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TEMPLATE_CATEGORIES.map((c) => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {TEMPLATE_CATEGORIES.find((c) => c.value === draft.category)?.hint}
                </p>
              </div>

              <div className="space-y-2">
                <Label>Language</Label>
                <Select
                  value={draft.language}
                  // Fixed once created, like the name: Meta refuses the change, and the opt-out
                  // label is checked against the language the template was created in.
                  disabled={mode === 'edit'}
                  onValueChange={(v) => patch({
                    language: v,
                    buttons: relabelOptOut(draft.buttons, draft.language, v),
                  })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TEMPLATE_LANGUAGES.map((l) => (
                      <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={draft.name}
                disabled={mode === 'edit'}
                onChange={(e) => patch({ name: slugifyTemplateName(e.target.value) })}
                placeholder="spring_launch"
              />
              <p className="text-xs text-muted-foreground">
                {mode === 'edit'
                  ? 'A template’s name cannot be changed after it is created.'
                  : 'Lowercase letters, numbers and underscores. Used internally, never shown to recipients.'}
              </p>
              <FieldError message={show('name')} />
            </div>

            {/* ---- header ---- */}
            <div className="space-y-2">
              <Label>Header</Label>
              <Select
                value={draft.headerType}
                onValueChange={(v) => {
                  // Switching type invalidates whatever the previous one held — including a sample
                  // still being prepared for it.
                  assetRequest.current += 1;
                  setPreparing(false);
                  setAssetError(null);
                  setShowUpload(false);
                  patch({
                    headerType: v as TemplateDraft['headerType'],
                    headerContent: '', headerSample: '', headerHandle: null, headerFilename: null,
                    headerAssetUid: null, headerPreviewUrl: null,
                  });
                  setSamplePreviewUrl(null);
                  // Then fill it from the media when it can be: an image header takes the
                  // selected photo, a video header the selected video.
                  const fill = assetsFor(v).find((a) => a.usable && a.selected)
                    ?? assetsFor(v).find((a) => a.usable);
                  if (fill && canUploadSamples) pickAsset(fill, v);
                }}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {HEADER_TYPES.map((h) => {
                    const blocked = MEDIA_HEADER_TYPES.includes(h.value) && !canUploadSamples;
                    return (
                      <SelectItem key={h.value} value={h.value} disabled={blocked}>
                        {h.label}{blocked ? ' — unavailable' : ''}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              {!canUploadSamples && (
                // The same sentence the server throws, so the user reads one explanation whichever
                // layer refuses.
                <p className="text-xs text-muted-foreground">
                  Image, video and document headers are unavailable on this server. Text headers and
                  headerless templates work normally.
                </p>
              )}
              <FieldError message={show('headerType')} />
            </div>

            {draft.headerType === 'TEXT' && (
              <div className="space-y-2">
                <Input
                  value={draft.headerContent}
                  maxLength={TEMPLATE_LIMITS.HEADER_TEXT}
                  onChange={(e) => patch({ headerContent: e.target.value })}
                  placeholder="New listing in {{1}}"
                />
                <p className="text-xs text-muted-foreground">
                  {draft.headerContent.length}/{TEMPLATE_LIMITS.HEADER_TEXT} — at most one variable, and it must be {'{{1}}'}.
                </p>
                <FieldError message={show('headerContent')} />
                {/\{\{\d+\}\}/.test(draft.headerContent) && (
                  <div className="space-y-1">
                    <Label className="text-xs">Example value for the header</Label>
                    <Input
                      value={draft.headerSample}
                      onChange={(e) => patch({ headerSample: e.target.value })}
                      placeholder="New Cairo"
                    />
                    <FieldError message={show('headerSample')} />
                  </div>
                )}
              </div>
            )}

            {MEDIA_HEADER_TYPES.includes(draft.headerType) && (
              <div className="space-y-2">
                {/* Always visible, not only before an upload: what happens to this file after review
                    is the natural question, and the answer depends on where it came from. A
                    generated asset is kept, so campaigns start with it; a hand-picked file is not. */}
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription className="text-xs">
                    {chosenAsset ? (
                      <>
                        WhatsApp reviews this {mediaNoun}, and campaigns using this template start with
                        it as their header. You can pick another one for each campaign.
                      </>
                    ) : (
                      <>
                        This {mediaNoun} is only a sample for WhatsApp&rsquo;s review. It won&rsquo;t be
                        sent in your campaigns. You&rsquo;ll choose the actual {mediaNoun} each time you
                        set up a campaign.
                      </>
                    )}
                  </AlertDescription>
                </Alert>

                {headerAssets.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground" dir="auto">
                      From your media{sourceMediaName ? ` “${sourceMediaName}”` : ''}
                    </Label>
                    <MediaAssetGrid
                      assets={headerAssets}
                      chosenUid={draft.headerAssetUid}
                      busy={preparing}
                      disabled={!canUploadSamples || uploading}
                      label={`Header ${mediaNoun}`}
                      onPick={(asset) => pickAsset(asset, draft.headerType)}
                    />
                  </div>
                )}

                {preparing && (
                  <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Loader2 className="h-3 w-3 animate-spin" /> Preparing it for WhatsApp&rsquo;s review…
                  </p>
                )}
                {!preparing && chosenAsset && draft.headerHandle && (
                  <p className="text-xs text-green-700">Ready for WhatsApp&rsquo;s review.</p>
                )}
                <FieldError message={assetError ?? undefined} />

                {headerAssets.length > 0 && !showUpload ? (
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    className="h-auto px-0 text-xs"
                    disabled={!canUploadSamples}
                    onClick={() => setShowUpload(true)}
                  >
                    Upload a different file instead
                  </Button>
                ) : (
                  <Input
                    type="file"
                    accept={SAMPLE_ACCEPT[draft.headerType]}
                    disabled={uploading || !canUploadSamples}
                    onChange={(e) => handleSampleUpload(e.currentTarget)}
                  />
                )}
                {(uploading || (draft.headerFilename && !chosenAsset)) && (
                  <p className="text-xs text-muted-foreground">
                    {uploading ? 'Uploading…' : `Sample: ${draft.headerFilename}`}
                  </p>
                )}
                <FieldError message={show('headerSample')} />
              </div>
            )}

            {/* ---- body ---- */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Message</Label>
                <Button type="button" variant="outline" size="sm" onClick={handleInsertVariable}>
                  <Plus className="h-3 w-3 mr-1" /> Variable
                </Button>
              </div>
              <Textarea
                ref={bodyRef}
                rows={6}
                value={draft.body}
                onChange={(e) => patch({ body: e.target.value })}
                placeholder="Hi {{name}}, we have a new listing that matches your budget of {{budget}}."
              />
              {/* Counted on the positional body, because that is what WhatsApp measures. */}
              <p className="text-xs text-muted-foreground">
                {normalized.body.length}/{TEMPLATE_LIMITS.BODY} — type {'{{name}}'}, {'{{budget}}'} or{' '}
                {'{{location}}'} to fill in each contact's details, or use + Variable.
              </p>
              <FieldError message={showBodyError} />
            </div>

            {variables.length > 0 && (
              <div className="space-y-3 rounded-md border p-3">
                <p className="text-xs text-muted-foreground">
                  For each variable, give WhatsApp an example and tell us which contact field fills it
                  when the message is actually sent.
                </p>
                {/* Samples and mappings are keyed by the token as typed, so they follow a variable
                    when an edit to the body renumbers it. Errors are keyed by the index. */}
                {variables.map(({ token, index }) => (
                  <div key={token} className="grid grid-cols-[96px_1fr_1fr] items-start gap-2">
                    <div className="mt-2 min-w-0">
                      <code className="block truncate text-xs" title={`{{${token}}}`}>{`{{${token}}}`}</code>
                      {token !== index && (
                        // The number every other screen will show for it, e.g. the mapping editor.
                        <span className="block text-[10px] text-muted-foreground">{`sent as {{${index}}}`}</span>
                      )}
                    </div>
                    <div className="space-y-1">
                      <Input
                        value={draft.bodySamples[token] ?? ''}
                        onChange={(e) => patch({
                          bodySamples: { ...draft.bodySamples, [token]: e.target.value },
                        })}
                        placeholder="Example"
                      />
                      <FieldError message={show(`bodySamples.${index}`)} />
                    </div>
                    <Select
                      // The normalised mapping, so a {{name}} the author never touched shows the
                      // field it was matched to.
                      value={normalized.variableMapping[index] ?? NO_FIELD}
                      onValueChange={(v) => patch({
                        // '' rather than a delete: it records "Not mapped" as a choice, so the
                        // token's name does not match it straight back to a field.
                        variableMapping: { ...draft.variableMapping, [token]: v === NO_FIELD ? '' : v },
                      })}
                    >
                      <SelectTrigger><SelectValue placeholder="Contact field" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NO_FIELD}>Not mapped</SelectItem>
                        {CONTACT_FIELDS.map((f) => (
                          <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            )}

            {/* ---- footer ---- */}
            <div className="space-y-2">
              <Label>Footer <span className="text-muted-foreground text-xs">(optional)</span></Label>
              <Input
                value={draft.footer}
                maxLength={TEMPLATE_LIMITS.FOOTER}
                onChange={(e) => patch({ footer: e.target.value })}
                placeholder="Reply STOP to unsubscribe"
              />
              <p className="text-xs text-muted-foreground">
                {draft.footer.length}/{TEMPLATE_LIMITS.FOOTER} — footers cannot contain variables.
              </p>
              <FieldError message={show('footer')} />
            </div>

            {/* ---- buttons ---- */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Buttons</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={draft.buttons.length >= TEMPLATE_LIMITS.BUTTONS_TOTAL}
                  onClick={() => patch({
                    buttons: [...draft.buttons, { type: 'QUICK_REPLY', text: '' }],
                  })}
                >
                  <Plus className="h-3 w-3 mr-1" /> Button
                </Button>
              </div>
              <FieldError message={show('buttons')} />

              {draft.buttons.map((button, i) => (
                <div key={i} className="space-y-2 rounded-md border p-3">
                  <div className="flex items-center gap-2">
                    <Select
                      value={button.type}
                      // The opt-out is a quick reply by definition: only a quick reply's tap comes
                      // back to us as a message.
                      disabled={button.optOut}
                      onValueChange={(v) => updateButton(i, {
                        type: v as ButtonType, url: undefined, phoneNumber: undefined, urlSample: undefined,
                      })}
                    >
                      <SelectTrigger className="w-[150px]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="QUICK_REPLY">Quick reply</SelectItem>
                        <SelectItem value="URL">Link</SelectItem>
                        <SelectItem value="PHONE_NUMBER">Call</SelectItem>
                      </SelectContent>
                    </Select>
                    {button.optOut ? (
                      <Select value={button.text} onValueChange={(v) => updateButton(i, { text: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {optOutLabelsFor(draft.language).map((label) => (
                            <SelectItem key={label} value={label}>{label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        value={button.text}
                        maxLength={TEMPLATE_LIMITS.BUTTON_TEXT}
                        onChange={(e) => updateButton(i, { text: e.target.value })}
                        placeholder="Button label"
                      />
                    )}
                    <Button type="button" variant="ghost" size="icon" onClick={() => moveButton(i, -1)}>
                      <ArrowUp className="h-4 w-4" />
                    </Button>
                    <Button type="button" variant="ghost" size="icon" onClick={() => moveButton(i, 1)}>
                      <ArrowDown className="h-4 w-4" />
                    </Button>
                    {button.optOut ? (
                      <Badge variant="secondary" className="shrink-0">Required</Badge>
                    ) : (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => patch({ buttons: draft.buttons.filter((_, j) => j !== i) })}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  {button.optOut && (
                    <p className="text-xs text-muted-foreground">
                      Required on every template. Tapping it unsubscribes the contact from all future
                      campaigns.
                    </p>
                  )}
                  <FieldError message={show(`buttons.${i}.text`)} />

                  {button.type === 'URL' && (
                    <div className="space-y-1">
                      <Input
                        value={button.url ?? ''}
                        onChange={(e) => updateButton(i, { url: e.target.value })}
                        placeholder="https://targetna.com/listing"
                      />
                      <FieldError message={show(`buttons.${i}.url`)} />
                      {/\{\{\d+\}\}/.test(button.url ?? '') && (
                        <>
                          <Input
                            value={button.urlSample ?? ''}
                            onChange={(e) => updateButton(i, { urlSample: e.target.value })}
                            placeholder="Example: https://targetna.com/listing/abc123"
                          />
                          <FieldError message={show(`buttons.${i}.urlSample`)} />
                        </>
                      )}
                    </div>
                  )}

                  {button.type === 'PHONE_NUMBER' && (
                    <div className="space-y-1">
                      <Input
                        value={button.phoneNumber ?? ''}
                        onChange={(e) => updateButton(i, { phoneNumber: e.target.value })}
                        placeholder="+201234567890"
                      />
                      <FieldError message={show(`buttons.${i}.phoneNumber`)} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* ---------------- preview ---------------- */}
          <div className="space-y-3">
            <Label className="text-xs text-muted-foreground">Preview</Label>
            <div className="sticky top-0">
              <WhatsAppMessagePreview
                template={{
                  body: normalized.body,
                  footer: draft.footer,
                  headerType: draft.headerType,
                  headerContent: draft.headerContent,
                  buttons: draft.buttons.map((b) => ({
                    type: b.type, text: b.text, url: b.url ?? null, phoneNumber: b.phoneNumber ?? null,
                  })),
                  variableMapping: normalized.variableMapping,
                  // The samples double as the preview's labels, so the bubble reads as a real
                  // message instead of a row of numbered placeholders.
                  metadata: { variableLabels: normalized.bodySamples },
                }}
                requirements={previewRequirements}
                recipient={null}
                headerText={draft.headerSample}
                headerMedia={MEDIA_HEADER_TYPES.includes(draft.headerType) ? {
                  // A file picked by hand previews from the browser; a generated asset from its own
                  // signed URL, since it is already ours.
                  previewUrl: samplePreviewUrl ?? (draft.headerAssetUid ? draft.headerPreviewUrl : null),
                  label: draft.headerFilename,
                  // Editing starts with no file in hand: the approved sample lives with Meta.
                  note: samplePreviewUrl || draft.headerAssetUid ? null : 'Upload a sample to preview it here',
                } : null}
              />
              {MEDIA_HEADER_TYPES.includes(draft.headerType) && (
                <p className="mt-2 text-center text-[11px] text-muted-foreground">
                  {draft.headerAssetUid
                    ? `Campaigns using this template start with this ${mediaNoun}. You can change it for each one.`
                    : `Sample ${mediaNoun} shown for preview only. Campaigns send the one you choose for them.`}
                </p>
              )}
            </div>
            {touched && errors.length > 0 && (
              <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-2">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                <p className="text-xs text-destructive">
                  {errors.length === 1 ? '1 thing needs fixing' : `${errors.length} things need fixing`} before this can be submitted.
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting || uploading || preparing}>
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {mode === 'create' ? 'Submit for review' : 'Save and resubmit'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
